// src/components/tutor/useTutorThread.ts
//
// Client-side hook that owns the tutor thread state: load history on mount,
// send messages, stream responses, expose status flags. Used by both the
// /tutor page and the FAB chat panel — they share the same thread.

'use client'

import type { LessonRef, SageDrillRef, VideoRef } from '@/lib/tutor/on-screen-material'
import { useCallback, useEffect, useRef, useState } from 'react'
import { parseSageQuotaHeaders } from '@/lib/tutor/sage-quota'

function parseToolCalls(raw: unknown): TutorToolCall[] | undefined {
  if (!Array.isArray(raw)) return undefined
  const out: TutorToolCall[] = []
  for (const e of raw) {
    if (!e || typeof e !== 'object') continue
    const o = e as Record<string, unknown>
    if (typeof o.id !== 'string' || typeof o.name !== 'string') continue
    if (o.tier !== 'auto' && o.tier !== 'soft_confirm' && o.tier !== 'hard_confirm') continue
    out.push({
      id: o.id,
      name: o.name,
      tier: o.tier,
      args: o.args,
      preview: o.preview,
    })
  }
  return out.length > 0 ? out : undefined
}

function parseToolResults(raw: unknown): TutorToolResult[] | undefined {
  if (!Array.isArray(raw)) return undefined
  const out: TutorToolResult[] = []
  for (const e of raw) {
    if (!e || typeof e !== 'object') continue
    const o = e as Record<string, unknown>
    if (typeof o.id !== 'string' || typeof o.ok !== 'boolean') continue
    out.push({
      id: o.id,
      ok: o.ok,
      result: o.result,
      error: typeof o.error === 'string' ? o.error : undefined,
      ranAt: typeof o.ranAt === 'string' ? o.ranAt : new Date().toISOString(),
    })
  }
  return out.length > 0 ? out : undefined
}

export type TutorToolCall = {
  id: string
  name: string
  tier: 'auto' | 'soft_confirm' | 'hard_confirm'
  args: unknown
  preview?: unknown
}

export type TutorToolResult = {
  id: string
  ok: boolean
  result?: unknown
  error?: string
  ranAt: string
}

export type TutorMessage = {
  id: string
  role: 'user' | 'assistant'
  content: string
  /** Whether this message is still streaming. Only the latest assistant row can be true. */
  streaming?: boolean
  pending?: boolean
  toolCalls?: TutorToolCall[]
  toolResults?: TutorToolResult[]
  createdAt?: string
}

export type PageContext = {
  path?: string
  questionId?: string
  surface?: string
  /** The lesson / video on screen, by id (src/lib/tutor/on-screen-material.ts). */
  lesson?: LessonRef
  video?: VideoRef
  /** A finished Sage drill the student asked about from its results screen. */
  drill?: SageDrillRef
}

export type TutorThreadState = {
  messages: TutorMessage[]
  loading: boolean
  sending: boolean
  error: string | null
  /** Sage messages left today (the daily fair-use window). null = unknown /
   *  uncapped (ADMIN). */
  dailyRemaining: number | null
  /** This tier's daily Sage cap. null when unknown. */
  dailyCap: number | null
}

export type UseTutorThread = TutorThreadState & {
  send: (text: string, pageContext?: PageContext) => Promise<void>
  refresh: () => Promise<void>
  clear: () => Promise<void>
}

export function useTutorThread(): UseTutorThread {
  const [messages, setMessages] = useState<TutorMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dailyRemaining, setDailyRemaining] = useState<number | null>(null)
  const [dailyCap, setDailyCap] = useState<number | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch('/api/tutor/thread', { cache: 'no-store' })
      if (!res.ok) {
        if (res.status === 401) {
          setMessages([])
          return
        }
        throw new Error(`Failed to load thread (${res.status})`)
      }
      // The daily allowance arrives on the LOAD too, not only on a reply, so
      // the student can see what a message costs before spending one. The GET
      // reads the limiter without consuming from it.
      const loadQuota = parseSageQuotaHeaders(res.headers)
      if (loadQuota.remaining !== null) setDailyRemaining(loadQuota.remaining)
      if (loadQuota.cap !== null) setDailyCap(loadQuota.cap)
      type RawMsg = {
        id: string
        role: 'user' | 'assistant'
        content: string
        toolCalls: unknown
        toolResults: unknown
        pending: boolean
        createdAt: string
      }
      const data = (await res.json()) as { messages: RawMsg[] }

      const parsed: TutorMessage[] = (data.messages ?? []).map((m) => ({
        id: m.id,
        role: m.role,
        content: m.content,
        pending: m.pending,
        createdAt: m.createdAt,
        toolCalls: parseToolCalls(m.toolCalls),
        toolResults: parseToolResults(m.toolResults),
      }))
      setMessages(parsed)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load thread')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
    return () => {
      abortRef.current?.abort()
    }
  }, [refresh])

  const send = useCallback(
    async (text: string, pageContext?: PageContext) => {
      const trimmed = text.trim()
      if (!trimmed || sending) return

      setError(null)
      setSending(true)

      // Optimistic insert: user's message + placeholder "thinking..." assistant.
      // The server runs auto tools server-side and may return a multi-part
      // response with toolCalls/toolResults; we render those once it arrives.
      const tempUserId = `temp-user-${Date.now()}`
      const tempAssistantId = `temp-asst-${Date.now() + 1}`
      setMessages((prev) => [
        ...prev,
        { id: tempUserId, role: 'user', content: trimmed },
        { id: tempAssistantId, role: 'assistant', content: '', streaming: true },
      ])

      abortRef.current?.abort()
      const ctrl = new AbortController()
      abortRef.current = ctrl

      try {
        const res = await fetch('/api/tutor/thread/message', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          cache: 'no-store',
          body: JSON.stringify({ message: trimmed, pageContext: pageContext ?? null }),
          signal: ctrl.signal,
        })

        // Surface the remaining daily Sage quota (headers present on the
        // streamed success response; absent on errors → left unchanged).
        const quota = parseSageQuotaHeaders(res.headers)
        if (quota.remaining !== null) setDailyRemaining(quota.remaining)
        if (quota.cap !== null) setDailyCap(quota.cap)

        // Non-streaming error path (auth, rate, fallback)
        const contentType = res.headers.get('Content-Type') ?? ''
        if (!contentType.includes('text/event-stream')) {
          const data = await res.json().catch(() => ({}))
          if (!res.ok) {
            let msg = 'Something went wrong sending that message.'
            if (data?.error === 'rate_limited') {
              msg = data.message ?? 'Slow down a moment — rate limit reached.'
              // A daily-cap hit means 0 left today.
              if (data?.blockedBy === 'day') {
                setDailyRemaining(0)
                if (typeof data?.dailyCap === 'number') setDailyCap(data.dailyCap)
              }
            } else if (data?.error === 'unauthorized') {
              msg = 'Sign in to chat with the tutor.'
            } else if (typeof data?.message === 'string') {
              // A coded refusal ({ error: 'school_ai_disabled', message }) —
              // show its sentence, not its code.
              msg = data.message
            } else if (typeof data?.error === 'string') {
              msg = data.error
            }
            setMessages((prev) =>
              prev.filter((m) => m.id !== tempUserId && m.id !== tempAssistantId),
            )
            setError(msg)
            return
          }
          // No-API-key fallback returns plain JSON. Treat the content as final.
          setMessages((prev) =>
            prev.map((m) =>
              m.id === tempAssistantId
                ? {
                    ...m,
                    content: typeof data.content === 'string' ? data.content : '',
                    streaming: false,
                  }
                : m,
            ),
          )
          if (!ctrl.signal.aborted) void refresh()
          return
        }

        // SSE streaming path
        const reader = res.body?.getReader()
        if (!reader) throw new Error('No response body')
        const decoder = new TextDecoder()
        let buf = ''
        let accumulated = ''
        let canonicalId: string | null = null
        const liveCalls: TutorToolCall[] = []
        const liveResults: TutorToolResult[] = []

        // Coalesce streamed text into ~20fps renders. Re-rendering the bubble
        // (markdown + KaTeX re-parse) and auto-scrolling on EVERY token is what
        // made the reply look jumpy and flash raw ** / $...$ markup. Tool and
        // done events flush immediately (force) so they're never delayed.
        let lastFlush = 0
        const FLUSH_MS = 50
        const flushBubble = () => {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === tempAssistantId
                ? {
                    ...m,
                    id: canonicalId ?? m.id,
                    content: accumulated,
                    streaming: !canonicalId, // false once we get `done`
                    toolCalls: liveCalls.length > 0 ? [...liveCalls] : undefined,
                    toolResults: liveResults.length > 0 ? [...liveResults] : undefined,
                  }
                : m,
            ),
          )
        }
        const updateBubble = (force = false) => {
          const now = Date.now()
          if (!force && now - lastFlush < FLUSH_MS) return
          lastFlush = now
          flushBubble()
        }

        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          buf += decoder.decode(value, { stream: true })
          const events = buf.split('\n\n')
          buf = events.pop() ?? ''
          for (const ev of events) {
            const line = ev.trim()
            if (!line.startsWith('data:')) continue
            const payload = line.slice(5).trim()
            if (!payload) continue
            let parsed: { type?: string; [key: string]: unknown }
            try {
              parsed = JSON.parse(payload)
            } catch {
              continue
            }
            switch (parsed.type) {
              case 'text': {
                if (typeof parsed.delta === 'string') {
                  accumulated += parsed.delta
                  updateBubble()
                }
                break
              }
              case 'tool_started': {
                const c = parsed.call as TutorToolCall | undefined
                if (c?.id) {
                  // Insert/replace by id (no result yet → ToolCard shows loader)
                  const idx = liveCalls.findIndex((x) => x.id === c.id)
                  if (idx >= 0) liveCalls[idx] = c
                  else liveCalls.push(c)
                  updateBubble(true)
                }
                break
              }
              case 'tool_proposed': {
                const c = parsed.call as TutorToolCall | undefined
                if (c?.id) {
                  const idx = liveCalls.findIndex((x) => x.id === c.id)
                  if (idx >= 0) liveCalls[idx] = c
                  else liveCalls.push(c)
                  updateBubble(true)
                }
                break
              }
              case 'tool_result': {
                if (typeof parsed.id === 'string') {
                  liveResults.push({
                    id: parsed.id,
                    ok: parsed.ok === true,
                    result: parsed.result,
                    error: typeof parsed.error === 'string' ? parsed.error : undefined,
                    ranAt:
                      typeof parsed.ranAt === 'string' ? parsed.ranAt : new Date().toISOString(),
                  })
                  updateBubble(true)
                }
                break
              }
              case 'round_complete': {
                // Visual nicety: nothing required here. Could reset a
                // round indicator if we add one to the UI later.
                break
              }
              case 'done': {
                if (typeof parsed.messageId === 'string') {
                  canonicalId = parsed.messageId
                }
                updateBubble(true)
                break
              }
              case 'error': {
                setError(typeof parsed.message === 'string' ? parsed.message : 'Stream failed')
                break
              }
              default:
                break
            }
          }
        }

        // Stream finished — flush the final content (the throttle may have
        // skipped the last tokens) and clear the streaming flag.
        if (!canonicalId) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === tempAssistantId ? { ...m, content: accumulated, streaming: false } : m,
            ),
          )
        }

        if (!ctrl.signal.aborted) void refresh()
      } catch (err) {
        if ((err as { name?: string })?.name === 'AbortError') return
        setError(err instanceof Error ? err.message : 'Send failed')
        setMessages((prev) =>
          prev.map((m) => (m.id === tempAssistantId ? { ...m, streaming: false } : m)),
        )
      } finally {
        setSending(false)
      }
    },
    [refresh, sending],
  )

  const clear = useCallback(async () => {
    setError(null)
    try {
      const res = await fetch('/api/tutor/thread/clear', {
        method: 'POST',
        cache: 'no-store',
      })
      if (!res.ok) throw new Error('Clear failed')
      setMessages([])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Clear failed')
    }
  }, [])

  return { messages, loading, sending, error, dailyRemaining, dailyCap, send, refresh, clear }
}
